import { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Zap, Settings, ChevronLeft, ChevronRight, MessageCircle } from 'lucide-react';
import { WHATSAPP_NUMBER } from '../../config';
import { PRODUCT_ICONS, PRODUCT_COLORS, PRODUCT_FEATURES, PRODUCT_GALLERIES, DEFAULT_FEATURES } from './productData';

export default function ProductDetailCard({ product, index, enquireLabel, onEnquire }) {
  const [galleryIdx, setGalleryIdx] = useState(0);
  const colors = PRODUCT_COLORS[product.id] || PRODUCT_COLORS.rotavator;
  const gallery = PRODUCT_GALLERIES[product.id] || null;
  const features = PRODUCT_FEATURES[product.id] || DEFAULT_FEATURES;
  const isEven = index % 2 === 0;
  const activeImg = gallery ? gallery[galleryIdx].url : product.image;

  const changeImage = (direction) => {
    if (!gallery) return;
    setGalleryIdx((galleryIdx + direction + gallery.length) % gallery.length);
  };

  return (
    <motion.div
      id={product.id}
      data-testid={`product-detail-${product.id}`}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: 0.1 }}
      className="bg-white rounded-2xl overflow-hidden shadow-sm border"
      style={{ borderColor: '#E5E7EB' }}
    >
      <div className={`grid grid-cols-1 lg:grid-cols-2 ${isEven ? '' : 'lg:flex-row-reverse'}`}>
        {/* Image with Gallery */}
        <div
          className={`relative ${!isEven ? 'lg:order-2' : ''} overflow-hidden`}
          style={{ minHeight: '320px' }}
        >
          <img
            src={activeImg}
            alt={product.name}
            className="w-full h-full object-cover absolute inset-0 transition-all duration-500"
            style={{ minHeight: '320px' }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <div className="absolute inset-0" style={{ background: `linear-gradient(to ${isEven ? 'right' : 'left'}, ${colors.bg}cc, transparent)` }} />
          <div className="absolute inset-0 flex items-end p-8">
            <div>
              <div className="text-4xl mb-2">{PRODUCT_ICONS[product.id]}</div>
              <div className="font-heading font-black text-3xl text-white">{product.name}</div>
              <div className="text-sm mt-1" style={{ color: '#FCD34D' }}>{product.tagline}</div>
            </div>
          </div>

          {gallery && (
            <>
              <button
                onClick={() => changeImage(-1)}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full flex items-center justify-center transition-all hover:scale-110"
                style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
                aria-label="Previous image"
              >
                <ChevronLeft size={18} className="text-white" />
              </button>
              <button
                onClick={() => changeImage(1)}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full flex items-center justify-center transition-all hover:scale-110"
                style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
                aria-label="Next image"
              >
                <ChevronRight size={18} className="text-white" />
              </button>

              <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-2 px-4">
                {gallery.map((img, gi) => (
                  <button
                    key={img.label}
                    onClick={() => setGalleryIdx(gi)}
                    className="rounded overflow-hidden border-2 transition-all duration-200 hover:scale-110"
                    style={{
                      borderColor: gi === galleryIdx ? '#FCD34D' : 'rgba(255,255,255,0.4)',
                      width: '40px',
                      height: '30px',
                    }}
                    aria-label={img.label}
                  >
                    <img src={img.url} alt={img.label} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>

              <div className="absolute top-3 right-3 px-2 py-0.5 rounded-full text-xs font-bold text-white"
                style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                {galleryIdx + 1}/{gallery.length}
              </div>
            </>
          )}
        </div>

        {/* Content */}
        <div className={`p-8 lg:p-10 ${!isEven ? 'lg:order-1' : ''}`}>
          <p className="text-base leading-relaxed mb-6" style={{ color: '#4A554A' }}>{product.description}</p>

          <div className="mb-6">
            <h4 className="font-heading font-semibold text-sm uppercase tracking-wider mb-3" style={{ color: '#0A3F22' }}>
              Technical Specifications
            </h4>
            <div className="grid grid-cols-2 gap-2">
              {product.specs.map((spec) => (
                <div
                  key={spec}
                  className="flex items-center gap-2 p-2.5 rounded-lg text-sm font-medium"
                  style={{ backgroundColor: colors.light, color: colors.bg }}
                >
                  <Settings size={13} style={{ color: colors.accent, flexShrink: 0 }} />
                  {spec}
                </div>
              ))}
            </div>
          </div>

          <div className="mb-8">
            <h4 className="font-heading font-semibold text-sm uppercase tracking-wider mb-3" style={{ color: '#0A3F22' }}>
              Key Features
            </h4>
            <ul className="space-y-2">
              {features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm" style={{ color: '#4A554A' }}>
                  <CheckCircle size={15} className="flex-shrink-0 mt-0.5" style={{ color: '#1B6B3D' }} />
                  {f}
                </li>
              ))}
            </ul>
          </div>

          <div className="flex gap-3">
            <button
              data-testid={`product-page-enquire-${product.id}`}
              onClick={() => onEnquire(product)}
              className="flex-1 py-3 rounded-lg font-heading font-bold text-white text-sm transition-all duration-200 hover:opacity-90 flex items-center justify-center gap-2"
              style={{ backgroundColor: colors.bg }}
            >
              <Zap size={16} />
              {enquireLabel}
            </button>
            <a
              href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(`Hello, I am interested in ARV ${product.name}. Please share price and availability.`)}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`product-whatsapp-${product.id}`}
              className="px-4 py-3 rounded-lg font-heading font-bold text-white text-sm transition-all duration-200 hover:opacity-90 flex items-center justify-center gap-2"
              style={{ backgroundColor: '#25D366' }}
            >
              <MessageCircle size={16} />
              WhatsApp
            </a>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
